import { Box, Button, ButtonGroup, Card, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PageHeader } from '../../app/AppStyles';
import { fetchTimetables, postTimetable, putTimetable, resetTimetableManagementStatus } from './timetableManagementSlice';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    width: { xs: '90%', md: '40%' },
    maxHeight: '90%',
    boxShadow: 24,
};

export default function TimetableEditCard({ handleClose, addNew }) {
    const dispatch = useDispatch();

    const { user } = useSelector((state) => state.user);
    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { timetablePut, timetablePost, selectedTimetable } = useSelector((state) => state.management.timetable);
    const { siomSettings } = useSelector((state) => state.settings.siom);
    const { activeDepartment } = useSelector((state) => state.settings.theme);

    const [changed, setChanged] = useState(false);

    // Put status callback
    useEffect(() => {
        if (timetablePut.status === "success") {
            dispatch(resetTimetableManagementStatus());
            dispatch(fetchTimetables());
            handleClose();
        } else if (timetablePut.status === "failed") {
            dispatch(resetTimetableManagementStatus());
        };
    }, [dispatch, handleClose, timetablePut.status]);

    // Post status callback
    useEffect(() => {
        if (timetablePost.status === "success") {
            dispatch(resetTimetableManagementStatus());
            dispatch(fetchTimetables());
            handleClose();
        } else if (timetablePost.status === "failed") {
            dispatch(resetTimetableManagementStatus());
        };
    }, [dispatch, handleClose, timetablePost.status]);

    // Character limits
    const nameLength = 256;

    // Property states
    const [newTimetable, setNewTimetable] = useState({});
    const [errorName, setErrorName] = useState(null);

    // Selected period state update 
    useEffect(() => {
        if (!addNew && selectedTimetable) {
            setNewTimetable(selectedTimetable);
        }
    }, [selectedTimetable, addNew]);

    // Keep track of changed state 
    useEffect(() => {
        if (newTimetable !== selectedTimetable) {
            setChanged(true);
        } else {
            setChanged(false);
        }
    }, [newTimetable, selectedTimetable]);

    // Update click function (check property character limits etc.)
    const updateClick = () => {
        let tempObject = { ...newTimetable }; // Needed to circumvent weird read only property error

        if (!newTimetable.name) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        if (newTimetable.name && newTimetable.name?.length > nameLength) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_TOO_LONG} ${nameLength}`);

        setErrorName(null);

        tempObject.modifiedBy = `AP/${user.number}`;

        dispatch(putTimetable(tempObject));
    };

    // Add click function (check property character limits etc.)
    const addClick = () => {
        let tempObject = { ...newTimetable }; // Needed to circumvent weird read only property error

        if (!newTimetable.name) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_EMPTY}`);
        if (newTimetable.name && newTimetable.name?.length > nameLength) return setErrorName(`${resourceFinal[appLanguage].messages.ERROR_PROPERTY_TOO_LONG} ${nameLength}`);

        setErrorName(null);

        tempObject.createdBy = `AP/${user.number}`;
        tempObject.departmentId = siomSettings.useMultipleDepartments && activeDepartment ? activeDepartment[0]?.id : "00000000-0000-0000-0000-000000000000";

        dispatch(postTimetable(tempObject));
    };

    return (
        <Box sx={{ ...modalStyle }}>
            <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                <Stack spacing={1} sx={{ flex: '1 1 auto' }}>
                    <Typography variant="h6" sx={{ ...PageHeader }}>{resourceFinal[appLanguage].words.TIMETABLE} {addNew ? resourceFinal[appLanguage].words.ADD.toLowerCase() : resourceFinal[appLanguage].words.EDIT.toLowerCase()}</Typography>
                    <TextField
                        fullWidth required
                        defaultValue={selectedTimetable?.name}
                        label={resourceFinal[appLanguage].words.NAME}
                        onChange={(event) => setNewTimetable({
                            ...newTimetable,
                            name: event.target.value
                        })}
                        error={errorName !== null}
                        helperText={errorName}
                        variant="outlined"
                    />
                    <TextField
                        fullWidth
                        defaultValue={selectedTimetable?.description}
                        label={resourceFinal[appLanguage].words.DESCRIPTION}
                        onChange={(event) => setNewTimetable({
                            ...newTimetable,
                            description: event.target.value
                        })}
                        variant="outlined"
                    />
                    <FormControlLabel
                        label={resourceFinal[appLanguage].words.AVAILABLE}
                        control={<Checkbox
                            checked={newTimetable?.available ? newTimetable?.available : false}
                            onChange={(event) => setNewTimetable({
                                ...newTimetable,
                                available: event.target.checked ? true : false
                            })}
                        />}
                    />
                    {(timetablePut.data === false || timetablePost.data === false) && <>
                        <Typography variant="body1" color="error" sx={{ textAlign: 'center' }}>{resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}</Typography>
                    </>}
                    <ButtonGroup sx={{ justifyContent: 'center' }}>
                        <Button variant="outlined" color="error" onClick={handleClose}>{resourceFinal[appLanguage].words.CANCEL}</Button>
                        {!addNew && <Button disabled={!changed} variant="outlined" color="primary" onClick={updateClick}>{resourceFinal[appLanguage].words.SAVE}</Button>}
                        {addNew && <Button variant="outlined" color="primary" onClick={addClick}>{resourceFinal[appLanguage].words.ADD}</Button>}
                    </ButtonGroup>
                </Stack>
            </Card>
        </Box>
    );
}
